import { ICustomBinding } from "#/DefaultViewExtensionInterfaces";
import Control from '@/Controls/Control';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import Utils from '@/Infrastructure/CustomBindingUtilities';
import { CustomBindingRenderHooks } from '#/DefaultViewExtensionInterfaces';
import AsyncTypeGuards from '@/Utility/AsyncTypeGuards';
import RADApplication from '@/Management/RADApplication';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';

type Options = {
    uiGenerator: string;
    viewModel: ControlViewModel;
    useDefaultDisposing: boolean;
    subcontrol: string;
    hooks?: CustomBindingRenderHooks;
}

export default class ControlBinding implements ICustomBinding {

    public initBinding() {
        ko.bindingHandlers.control = {
            init: (node: HTMLElement, valueAccessor: () => Options, bindings: KnockoutAllBindingsAccessor, view: any, bindingContext: KnockoutBindingContext) => {
                const element = $(node);
                const options = valueAccessor();

                const uiGenerator = options.uiGenerator;
                let viewModel = options.viewModel;
                const defaultDisposingHandler = options.useDefaultDisposing !== false;


                if (bindingContext.$root != null) {
                    let rootVM = bindingContext.$root.viewModel || bindingContext.$root.uiElement.context;
                    // wenn das HauptViewModel aus dem Context bereits disposed wurde, dann muss auch kein Control mehr gerendert werden!
                    if (rootVM == null || rootVM.isDisposed) {
                        rootVM = null;
                        return;
                    }
                }

                if (viewModel == null) {
                    throw 'ViewModel for Control "' + options.uiGenerator + '" is not defined';
                }

                if (_.isFunction(viewModel)) {
                    viewModel = viewModel();
                }

                const control = Control.createInstance(uiGenerator, viewModel, element);
                if (control == null) {
                    return;
                }

                this.triggerHook(options.hooks, 'beforeRender', control, bindingContext);

                // e.g. "subcontrol: 'fancyTreeControl'" sets control to the variable 'fancyTreeControl' in the base-control (e.g. MGSelectorControl)
                // this mechanism enables that a base-control can influence its sub-controls
                if (options.hasOwnProperty('subcontrol')) {
                    bindingContext.$root.uiElement[options.subcontrol] = control;
                }

                if (defaultDisposingHandler) {
                    Utils.registerDisposable(control, bindings, bindingContext);
                }

                if (AsyncTypeGuards.isAsyncTupleProvider(control)) {
                    control.getTupleAsync().then(() => {
                        this.triggerHook(options.hooks, 'afterRender', control, bindingContext);
                    }).catch(e => {
                        this.handleError(e);
                        this.triggerHook(options.hooks, 'onFailure', control, bindingContext, e);
                    });
                } else {
                    try {
                        control.getTuple();
                        this.triggerHook(options.hooks, 'afterRender', control, bindingContext);
                    } catch (e) {
                        this.handleError(e);
                        this.triggerHook(options.hooks, 'onFailure', control, bindingContext, e);
                    }
                }
            }
        }
    }

    private handleError(error?: Error) {
        console.error(error);

        const errorMessageBox = MessageBoxViewModel.createErrorMessageBox(null, 'GENERIC_ERROR', error?.stack);
        RADApplication.getInstance().showMessageBox(errorMessageBox);
    }

    private triggerHook<T extends keyof CustomBindingRenderHooks<Control>>(
        hooks: CustomBindingRenderHooks<Control>,
        hookName: T,
        ...params: Parameters<CustomBindingRenderHooks<Control>[T]>
    ) {
        if (!hooks)
            return;

        const hook = hooks[hookName];
        if (!hook || typeof hook !== 'function')
            return;

        hook(params[0], params[1]);
    }
}